/**
 * Program Predecessor Conversion Utilities
 *
 * Shared utilities for converting program_predecessor values between RPN IDs and RPN aliases.
 * - ID to Alias: Used when reading data from backend to display to users
 * - Alias to ID: Used when submitting data back to backend
 */

import { FIRST_GENERATION_LABEL } from '../constants/appConstants';

/**
 * Converts a program_predecessor value from RPN ID to RPN alias (for display).
 * If the value is FIRST_GENERATION_LABEL or null, returns it unchanged.
 * If the value is a numeric RPN ID, converts it to the corresponding alias.
 * If the value is already an alias or not found, returns it unchanged.
 *
 * @param predecessorValue - The program_predecessor value to convert (may be RPN ID or alias)
 * @param rpnIdToAliasMap - Map of RPN ID to alias for lookup
 * @returns The converted value (alias) or original value if conversion not needed/possible
 *
 * @example
 * ```typescript
 * const map = new Map([[12345, "Echo Show Gen 3"]]);
 * const converted = convertProgramPredecessorToAlias("12345", map);
 * // Returns "Echo Show Gen 3"
 * ```
 */
export const convertProgramPredecessorToAlias = (
  predecessorValue: string | null,
  rpnIdToAliasMap: Map<number, string>
): string | null => {
  // If null, undefined, or FIRST_GENERATION_LABEL, leave as is
  if (!predecessorValue || predecessorValue === FIRST_GENERATION_LABEL) {
    return predecessorValue;
  }

  // Try to convert to number - if it's a valid RPN ID, look up the alias
  const predecessorId = Number(predecessorValue);

  if (!isNaN(predecessorId) && rpnIdToAliasMap.has(predecessorId)) {
    return rpnIdToAliasMap.get(predecessorId) || predecessorValue;
  }

  // If it's not a number or not found in the map, leave it as is (might already be an alias)
  return predecessorValue;
};

/**
 * Converts a program_predecessor value from RPN alias to RPN ID (for submission to backend).
 * If the value is FIRST_GENERATION_LABEL or null, returns it unchanged.
 * If the value is an RPN alias, converts it to the corresponding RPN ID (as string).
 * If the value is already an ID or not found, returns it unchanged.
 *
 * @param predecessorValue - The program_predecessor value to convert (may be alias or RPN ID)
 * @param rpnAliasToIdMap - Map of RPN alias to ID for lookup
 * @returns The converted value (RPN ID as string) or original value if conversion not needed/possible
 *
 * @example
 * ```typescript
 * const map = new Map([["Echo Show Gen 3", 12345]]);
 * const converted = convertProgramPredecessorToId("Echo Show Gen 3", map);
 * // Returns "12345"
 * ```
 */
export const convertProgramPredecessorToId = (
  predecessorValue: string | null,
  rpnAliasToIdMap: Map<string, number>
): string | null => {
  // If null, undefined, or FIRST_GENERATION_LABEL, leave as is
  if (!predecessorValue || predecessorValue === FIRST_GENERATION_LABEL) {
    return predecessorValue;
  }

  // Check if it's already a numeric ID - if so, leave as is
  const predecessorId = Number(predecessorValue);
  if (!isNaN(predecessorId)) {
    return predecessorValue;
  }

  // Try to find the ID by alias
  if (rpnAliasToIdMap.has(predecessorValue)) {
    const id = rpnAliasToIdMap.get(predecessorValue);
    return id !== undefined ? String(id) : predecessorValue;
  }

  // If not found in map, leave it as is
  return predecessorValue;
};

// Backward compatibility: Keep the original function name as alias to the new one
export const convertProgramPredecessorValue = convertProgramPredecessorToAlias;
